/*
 * Searay — frontend size grouping
 *   Phase 1: chains & bracelets   Phase 2: rings   Phase 3: bangles & oval hoops
 * ----------------------------------------------------------------------------
 * Products are synced from Unleashed as SEPARATE single-variant products, one
 * per size (18KROP7020CM / 18KROP7045CM ..., 9KDR678SIZEM / 9KDR678SIZEQ ...).
 * This module groups those siblings on the FRONTEND ONLY — nothing on the
 * Shopify backend changes:
 *   - Collection / search grids: collapse a group down to one representative card.
 *   - Upgraded product page: show a size selector that navigates between the
 *     sibling product pages (each size keeps its own price / stock / image).
 *
 * Five SKU rules, tried in order. Rule 0 is a fixed list; the rest are anchored at the
 * END of the SKU:
 *   0. LETTER  the initial charm / letter trace families (Jian, 2026-09-22). Here the
 *      choice is the LETTER, not a size: 9KTFL1A..Z are one charm, 9KTFL1A-AJ19..Z-AJ19
 *      one bracelet, and so on. Jian named the five groups by Unleashed bin location
 *      (TFL1-CHARMS / TFL1-BRAC / TFL1W-BRAC / TFL1-NECK / TFL1W-NECK); Shopify's bin
 *      metafield only holds "TFL1" for all 81 products, so the groups are read from the
 *      SKU shape, which splits them identically. 9KCR104 (cross) and 9KPN188 (heart)
 *      join the charms by name. Tried first because rule 1 would otherwise read the
 *      "-AJ19" / "-AJ45" as a length and group each letter only with itself.
 *      The 18k script letter pendants (18KDP18A..Y, Christina 2026-10-02) are a second
 *      family under the same rule, one group for all 21 letters.
 *   1. LENGTH  "<base><2 digits>CM"   -> chains, bracelets, anklets.
 *      Classified by the cm value (Christina's rule):
 *        < 25cm -> bracelet,   25-30cm -> anklet,   > 30cm -> chain
 *      so the 20cm bracelet and the 45-60cm chains sharing base "18KROP70"
 *      form TWO separate groups.
 *      The adjustable chains write the same length as "AJ<2 digits>" with no CM, some
 *      with a colour letter after it (18KSNW030AJ42 / AJ45, 9KTFL1A-AJ19W). Their group
 *      key keeps the AJ and the colour letter, so white never joins yellow.
 *   2. RING SIZE  "<base>SIZE<code>"  -> rings. The code is a UK letter with an
 *      optional half ("Q", "O1/2") or a numeric US size ("7.5"). The base already
 *      carries metal + colour (9KDR714Y), so "same ring, different size" is just
 *      "same base" — there is no per-category split as there is for lengths.
 *   3. DIMENSION  "<base><2 digits>X<2 digits>[MM]"  -> bangles and oval hoops.
 *      The pair is the piece's own measurement and IS the size choice: a bangle is
 *      54x47mm or 59x51mm, an oval hoop 18x14mm or 20x14mm. Measured 2026-08-26
 *      against the Unleashed catalogue (the source the resync restores from):
 *      21 families, 48 products — small next to lengths (402/1600) and ring sizes
 *      (386/1195), but without it those 48 list as near-identical rows with no way
 *      to choose. Two digits before the X is what keeps "9KHR3YX45MM" (a 3mm x 45mm
 *      measurement, one product) out: "3Y" is not a 2-digit number.
 *   4. HOOP DIAMETER  "<base ending in a letter><2 digits>"  -> round hoops and huggies.
 *      9KER150Y10 / 9KER150Y20 / 9KER150Y30 are one hoop in 10, 20 and 30mm. There is
 *      no marker at all, so this rule is the only one that has to be told what kind of
 *      product it is looking at: it fires ONLY when the product type names a hoop or
 *      huggie. Without that gate the letter chains (9KTFL1Z-AJ45 / -AJ19: lengths with
 *      no CM) and 9KDBA206W55/W60 would merge too — 35 non-hoop families measured on
 *      2026-09-14. The letter before the digits is what keeps bases that END in digits
 *      whole: 9KER660 is one product, not 9KER6 in 60mm. Reported by Michaela on
 *      2026-09-14 (search 9KER150Y showed three identical cards); 45 families / 165
 *      products on the live catalogue.
 *
 * SAFETY: a SKU matching neither rule is never grouped, and a product Shopify
 * holds as a real multi-variant product is left alone by both the grid and the
 * sibling lookup — it already offers every size on its own page.
 */
(function () {
  'use strict';

  // --- classification constants (no bare magic numbers in the logic below) ---
  var CATEGORY = {
    BRACELET: 'bracelet',
    ANKLET: 'anklet',
    CHAIN: 'chain',
    RING: 'ring',
    DIMENSION: 'dimension',
    HOOP: 'hoop',
    LETTER: 'letter'
  };
  // Which SKU rule matched. Only LENGTH carries a weight that varies by size, so
  // the "Weight by length" row is driven off this rather than off the category.
  var KIND = {
    LENGTH: 'length',
    RING_SIZE: 'ring-size',
    DIMENSION: 'dimension',
    HOOP_DIAMETER: 'hoop-diameter',
    LETTER: 'letter'
  };

  // Rule 0 — the letter families. Each is a SKU prefix + one letter + an optional tail;
  // the tail picks the group, and an unknown tail is left to the later rules rather than
  // guessed at. `groups` is keyed by the tail: `key` names the group and `title` is shown
  // on the one card a grid keeps for it. Predictive search returns at most 10 products
  // and a group holds up to 27, so each family names where the product page reads its
  // full letter list from (see fetchLetterSiblings).
  //
  // Trace chains (Jian, 2026-09-22): only the five tails below exist (checked against
  // every 9KTFL1* SKU); `key` is her Unleashed bin location name. Every one of them sits
  // in the initial charms collection.
  var TRACE_LETTERS = {
    re: /^9KTFL1([A-Z])(-AJ\d{2}W?)?$/i,
    groups: {
      '': { key: 'TFL1-CHARMS', title: '9k YG Initial Charms' },
      '-AJ19': { key: 'TFL1-BRAC', title: '9k YG Letter Trace Bracelet (1.1mm)' },
      '-AJ19W': { key: 'TFL1W-BRAC', title: '9k WG Letter Trace Bracelet (1.1mm)' },
      '-AJ45': { key: 'TFL1-NECK', title: '9k YG Letter Trace Chain (1.1mm)' },
      '-AJ45W': { key: 'TFL1W-NECK', title: '9k WG Letter Trace Chain (1.1mm)' }
    },
    collectionUrl: '/collections/initial-charms-necklace-and-bracelets/products.json?limit=250'
  };
  // 18k script letter pendants (Christina, 2026-10-02: "same thing we did for 9KTFL1"),
  // 18KDP18A..Y, 21 letters. The diamond weight differs per letter, so the group title
  // leaves it out. Their collection ("Initials") only holds letters in stock, so the
  // list comes from a search on the prefix instead (sections/searay-sku-list.liquid).
  var SCRIPT_LETTERS = {
    re: /^18KDP18([A-Z])()$/i,
    groups: {
      '': { key: 'DP18', title: '18k WG Diamond Script Letter Pendant (20mm)' }
    },
    searchTerm: '18KDP18'
  };
  var LETTER_FAMILIES = [TRACE_LETTERS, SCRIPT_LETTERS];
  // Non-letter charms Jian put in the trace charm group. They sort after Z.
  var LETTER_GROUP_EXTRAS = {
    '9KCR104': { family: TRACE_LETTERS, tail: '', value: 'CROSS', label: 'Cross' },
    '9KPN188': { family: TRACE_LETTERS, tail: '', value: 'HEART', label: 'Heart' }
  };
  var LETTER_EXTRA_SORT_OFFSET = 100;
  var SKU_LIST_SECTION = 'searay-sku-list';
  var LETTER_SELECTOR_LABEL = 'Letter';
  var LETTER_CARD_NOTE = 'Available in a range of letters';
  var SIZE_CARD_NOTE = 'Available in a range of sizes';

  var ANKLET_MIN_CM = 25; // 25-30cm is an anklet; below -> bracelet, above -> chain
  var ANKLET_MAX_CM = 30;

  // Trailing length before "CM". Bases on this store themselves end in digits
  // (...ROP70, ...BELY040), so the match is capped at three digits rather than taking
  // the whole trailing run: on "18KROP7045CM" greedy-3 reads "045" -> 45, and the base
  // keeps its own 70.
  //
  // Two digits is therefore the DEFAULT read, and three is only tried when the pair
  // comes back "00" — which is exactly how a 3-digit length presents ("…100CM"). A plain
  // greedy /(\d{2,3})CM$/ cannot be used: on 18KROP7045CM it takes "045" and leaves the
  // base as 18KROP7, so a family with both 2- and 3-digit lengths splits in two.
  //
  // The old rule was two digits only. It read the 100cm chains as "00", and a zero length
  // is discarded, so they were dropped from their own family: 925MAG040100CM is live and
  // has live 20/25/45cm siblings, and the 100cm option simply never appeared in the
  // selector (audit 2026-08-26; the only other case, 9KSNY030100CM, is obsolete).
  var SKU_LENGTH_RE = /(\d{2})CM$/i;
  var SKU_LENGTH_LONG_RE = /(\d{3})CM$/i;
  // Adjustable chains: "AJ" + cm, then an optional colour letter. Measured 2026-09-14
  // against the Unleashed catalogue: 19/42/45/50/65/90cm, the only suffix letter is W,
  // and "…AJ50CM" is read by the CM rule above. Three live families (18KSNW030,
  // 18KBLW035, 18KGDY040) listed as identical single cards per length without it.
  var SKU_ADJUSTABLE_LENGTH_RE = /AJ(\d{2})([A-Z]?)$/i;
  var CARD_SKU_ADJUSTABLE_LENGTH_RE = /-?AJ(\d{2})\s*$/i;
  var ADJUSTABLE_MARKER = 'AJ';
  // Trailing "...45cm" / "...100cm" on a product TITLE (with or without punctuation).
  // Greedy is safe here because the match is deleted, never split into base + size.
  var TITLE_LENGTH_RE = /\s*\(?\s*\d{2,3}\s?cm\)?\s*$/i;
  // Same trailing length at the end of the card's rendered "SKU: <code>" line.
  var CARD_SKU_LENGTH_RE = /(\d{2})CM\s*$/i;
  var CARD_SKU_LENGTH_LONG_RE = /(\d{3})CM\s*$/i;

  // Trailing ring size: "SIZEQ", "SIZEO1/2", "SIZE7.5". Verified 2026-08-19 against
  // the whole published catalogue — every SKU containing "SIZE" ends in one of these
  // forms, so the anchor never has to cope with a size buried mid-code.
  var RING_SIZE_BODY = '(\\d+(?:\\.\\d+)?|[A-Z](?:1\\/2)?)';
  var SKU_RING_SIZE_RE = new RegExp('SIZE' + RING_SIZE_BODY + '$', 'i');
  // Trailing ". Size Q" / " Size S." on a product TITLE. Only about a quarter of split
  // ring titles carry it, and the rest are already clean, so this only has to be safe.
  var TITLE_RING_SIZE_RE = /[\s.,;:-]*\bsizes?\s*(?:\d+(?:\.\d+)?|[a-z](?:\s?1\/2)?)\s*\.?\s*$/i;
  var CARD_SKU_RING_SIZE_RE = new RegExp('SIZE' + RING_SIZE_BODY + '\\s*$', 'i');

  // Trailing piece dimension: "54X47", "59X50MM". Both figures are 2 digits throughout
  // the catalogue (17-63mm). The "MM" is optional because the codes are inconsistent
  // about it within one family (9KBA15455X45 alongside 9KBFOH5-51X43MM).
  var SKU_DIMENSION_RE = /(\d{2})X(\d{2})(?:MM)?$/i;
  // Trailing "(59x51mm)" on a product TITLE. Anchored on the PAIR, so a trailing wire
  // width — "(3mm)" on the hoops, "(5mm)" on the bangles — is never mistaken for a size.
  // Unleashed writes the hoop dimension mid-title ("Oval Hoop Earrings 18x14mm Half
  // Round 3mm Wide"); stripping mid-string would also have to judge which measurement
  // is the size, so those titles are deliberately left whole.
  var TITLE_DIMENSION_RE = /\s*\(?\s*\d{2}\s?x\s?\d{2}\s?(?:mm)?\s?\)?\s*\.?\s*$/i;
  var CARD_SKU_DIMENSION_RE = /(\d{2})X(\d{2})(?:MM)?\s*$/i;

  // Trailing hoop diameter: the letter is part of the base (9KER150Y + 10) and is only
  // matched so the digits are known to follow a letter. Two digits is the whole range
  // (10-60mm live); "00" is not a size and is rejected by the numeric check.
  var SKU_HOOP_RE = /([A-Z])(\d{2})$/i;
  var CARD_SKU_HOOP_RE = /([A-Z])(\d{2})\s*$/i;
  // The gate: only a product whose type says hoop/huggie may use the rule above.
  var HOOP_TYPE_RE = /hoop|hugg/i;
  var MIN_HOOP_MM = 10;
  var MAX_HOOP_MM = 99;

  // Sorts 54x47 before 59x51 and 18x14 before 20x14 — the first figure decides, the
  // second only breaks a tie. Both are 2 digits, so the shift can never collide.
  var DIMENSION_MAJOR = 100;

  // Ring sizes sort A, A1/2, B, B1/2 ... so a half never lands before its whole size.
  // Numeric (US) sizes are a different scale entirely; they sort among themselves and
  // sit after the letters rather than interleaving on a meaningless numeric compare.
  var RING_LETTER_RE = /^([A-Z])(1\/2)?$/;
  var RING_HALF_STEPS = 2;
  var RING_NUMERIC_OFFSET = 1000;
  var RING_UNSORTABLE = 9999;

  // Weight field quirk: entered in Unleashed as "kg" but the number IS the gram value,
  // so a 25.65g rope chain comes back as weight 25650. See snippets/true-grams.liquid.
  //
  // That snippet can require weight_unit == 'kg' before applying the quirk; we cannot —
  // /products/<handle>.js returns `weight` but NOT `weight_unit`. So apply the quirk
  // unconditionally and bound the result instead: the only products whose weight we
  // read here are the cm-coded chains and bracelets, the same population the shipped
  // per-gram price already treats as kg-entered. A weight in any other unit lands far
  // outside the plausible per-piece range and is dropped, so a wrong unit shows as
  // missing, never as a wrong number (a genuine grams entry of 25.65 would yield
  // 0.026g and be rejected).
  var GRAMS_PER_KG = 1000;
  var GRAMS_DECIMALS = 2;
  var MIN_PLAUSIBLE_GRAMS = 0.1;
  var MAX_PLAUSIBLE_GRAMS = 500;
  // Stands in for a sibling with no weight on file, so the "Weight by length" cell stays
  // positionally aligned with "Available sizes" instead of shifting every later value
  // onto the wrong length.
  var MISSING_WEIGHT = '—';

  var PREDICTIVE_URL =
    (window.routes && window.routes.predictive_search_url) || '/search/suggest';

  function classify(cm) {
    if (cm < ANKLET_MIN_CM) return CATEGORY.BRACELET;
    if (cm <= ANKLET_MAX_CM) return CATEGORY.ANKLET;
    return CATEGORY.CHAIN;
  }

  function ringSortKey(code) {
    var letter = code.match(RING_LETTER_RE);
    if (letter) {
      return (letter[1].charCodeAt(0) - 'A'.charCodeAt(0)) * RING_HALF_STEPS +
        (letter[2] ? 1 : 0);
    }
    var n = parseFloat(code);
    return isNaN(n) ? RING_UNSORTABLE : RING_NUMERIC_OFFSET + n;
  }

  // Prefers the 2-digit read and falls back to 3 only when the pair is a zero length.
  // Returns the match itself so callers can both read the length and cut the exact span.
  function matchLength(text, shortRe, longRe) {
    var m = text.match(shortRe);
    if (m && parseInt(m[1], 10)) return m;
    m = text.match(longRe);
    return m && parseInt(m[1], 10) ? m : null;
  }

  function matchAdjustable(text, re) {
    var m = text.match(re);
    return m && parseInt(m[1], 10) ? m : null;
  }

  // Per-rule lookups. These were two-way ternaries on KIND.LENGTH, which silently sent
  // any third rule down the ring-size branch; a missing entry now shows up as a no-op
  // instead of as the wrong regex.
  var CARD_SKU_MATCH = {};
  CARD_SKU_MATCH[KIND.LENGTH] = function (t) {
    return matchLength(t, CARD_SKU_LENGTH_RE, CARD_SKU_LENGTH_LONG_RE) ||
      matchAdjustable(t, CARD_SKU_ADJUSTABLE_LENGTH_RE);
  };
  CARD_SKU_MATCH[KIND.RING_SIZE] = function (t) { return t.match(CARD_SKU_RING_SIZE_RE); };
  CARD_SKU_MATCH[KIND.DIMENSION] = function (t) { return t.match(CARD_SKU_DIMENSION_RE); };
  // The letter belongs to the base, so hand back a match that spans only the digits.
  CARD_SKU_MATCH[KIND.HOOP_DIAMETER] = function (t) { return matchHoop(t, CARD_SKU_HOOP_RE); };

  var TITLE_RE = {};
  TITLE_RE[KIND.LENGTH] = TITLE_LENGTH_RE;
  TITLE_RE[KIND.RING_SIZE] = TITLE_RING_SIZE_RE;
  TITLE_RE[KIND.DIMENSION] = TITLE_DIMENSION_RE;

  // Reads a card's SKU line back into the same canonical form parse() produces, so the
  // strip can be guarded on "this really is the representative's own size".
  var CARD_SKU_VALUE = {};
  CARD_SKU_VALUE[KIND.LENGTH] = function (m) { return String(parseInt(m[1], 10)); };
  CARD_SKU_VALUE[KIND.RING_SIZE] = function (m) { return m[1].toUpperCase(); };
  CARD_SKU_VALUE[KIND.DIMENSION] = function (m) { return m[1] + 'X' + m[2]; };
  CARD_SKU_VALUE[KIND.HOOP_DIAMETER] = function (m) { return String(parseInt(m[1], 10)); };

  // "9KBFOH5-59X50MM" leaves a dangling separator once the dimension comes off. Applied
  // per rule rather than to every base, so the cm and SIZE outputs cannot change shape.
  var TIDY_BASE = {};
  TIDY_BASE[KIND.DIMENSION] = function (b) { return b.replace(/[-\s]+$/, ''); };
  function tidyBase(kind, base) {
    var fn = TIDY_BASE[kind];
    return fn ? fn(base) : base;
  }

  // Matches a trailing "<letter><2 digits>" and returns a match positioned on the DIGITS
  // only (index + [0] cover just the two digits, [1] is the diameter), so the callers that
  // cut the size out of a SKU or a card line never take the base's last letter with it.
  function matchHoop(text, re) {
    var m = text.match(re);
    if (!m) return null;
    var mm = parseInt(m[2], 10);
    if (!(mm >= MIN_HOOP_MM && mm <= MAX_HOOP_MM)) return null;
    var digits = [m[2], m[2]];
    digits.index = m.index + m[1].length;
    digits.input = text;
    return digits;
  }

  function isHoopType(type) {
    return HOOP_TYPE_RE.test(String(type || ''));
  }

  // Parse a SKU into a group descriptor, or null when no rule matches — which is
  // how every non-sized product (pendants, studs, one-size pieces) is left untouched.
  // `type` is the product type; only the hoop rule reads it. Shape:
  //   base     shared product code            "18KROP70" / "9KDR678"
  //   kind     which rule matched             KIND.*
  //   category grouping category              CATEGORY.*
  //   key      grouping key (base + category)
  //   value    this product's size, canonical "45" / "O1/2"   (dedupe + current-match)
  //   label    how that size reads on screen  "45cm" / "O1/2"
  //   sort     position within the group
  //   cm       length in cm, LENGTH only (0 for rings)
  function parse(sku, type) {
    if (!sku) return null;
    var s = String(sku).toUpperCase().trim();
    return parseLetter(s) || parseLength(s) || parseRingSize(s) || parseDimension(s) ||
      parseHoopDiameter(s, type);
  }

  // Rule 0. `title` is extra to the shared shape: the group's own name for the grid card.
  function parseLetter(s) {
    var family, tail, value, label, sort;
    var extra = LETTER_GROUP_EXTRAS[s];
    if (extra) {
      family = extra.family;
      tail = extra.tail;
      value = extra.value;
      label = extra.label;
      sort = LETTER_EXTRA_SORT_OFFSET + Object.keys(LETTER_GROUP_EXTRAS).indexOf(s);
    } else {
      var m = null;
      for (var i = 0; i < LETTER_FAMILIES.length && !m; i++) {
        family = LETTER_FAMILIES[i];
        m = s.match(family.re);
      }
      if (!m) return null;
      tail = m[2] || '';
      value = m[1];
      label = m[1];
      sort = m[1].charCodeAt(0) - 'A'.charCodeAt(0);
    }
    var group = family.groups[tail];
    if (!group) return null;
    return {
      base: group.key,
      kind: KIND.LETTER,
      category: CATEGORY.LETTER,
      key: group.key + '|' + CATEGORY.LETTER,
      value: value,
      label: label,
      sort: sort,
      cm: 0,
      title: group.title,
      family: family
    };
  }

  function parseLength(s) {
    var m = matchLength(s, SKU_LENGTH_RE, SKU_LENGTH_LONG_RE);
    var variant = '';
    var base;
    if (m) {
      base = s.slice(0, m.index);
    } else {
      m = matchAdjustable(s, SKU_ADJUSTABLE_LENGTH_RE);
      if (!m) return null;
      // "9KTFL1N-AJ19" searches siblings as "9KTFL1N"; the marker and colour letter move
      // into the key instead, so they still separate W from yellow and AJ from CM codes.
      base = s.slice(0, m.index).replace(/[-\s]+$/, '');
      variant = ADJUSTABLE_MARKER + m[2];
    }
    if (!base) return null;
    var cm = parseInt(m[1], 10);
    var category = classify(cm);
    return {
      base: base,
      kind: KIND.LENGTH,
      category: category,
      key: base + variant + '|' + category,
      value: String(cm),
      label: cm + 'cm',
      sort: cm,
      cm: cm
    };
  }

  function parseRingSize(s) {
    var m = s.match(SKU_RING_SIZE_RE);
    if (!m) return null;
    var base = s.slice(0, m.index);
    if (!base) return null;
    var code = m[1];
    return {
      base: base,
      kind: KIND.RING_SIZE,
      category: CATEGORY.RING,
      key: base + '|' + CATEGORY.RING,
      value: code,
      label: code,
      sort: ringSortKey(code),
      cm: 0
    };
  }

  function parseDimension(s) {
    var m = s.match(SKU_DIMENSION_RE);
    if (!m) return null;
    var base = tidyBase(KIND.DIMENSION, s.slice(0, m.index));
    if (!base) return null;
    var w = m[1];
    var h = m[2];
    return {
      base: base,
      kind: KIND.DIMENSION,
      category: CATEGORY.DIMENSION,
      key: base + '|' + CATEGORY.DIMENSION,
      value: w + 'X' + h,
      label: w + 'x' + h + 'mm',
      sort: parseInt(w, 10) * DIMENSION_MAJOR + parseInt(h, 10),
      cm: 0
    };
  }

  function parseHoopDiameter(s, type) {
    if (!isHoopType(type)) return null;
    var m = matchHoop(s, SKU_HOOP_RE);
    if (!m) return null;
    var base = s.slice(0, m.index);
    if (!base) return null;
    var mm = parseInt(m[1], 10);
    return {
      base: base,
      kind: KIND.HOOP_DIAMETER,
      category: CATEGORY.HOOP,
      key: base + '|' + CATEGORY.HOOP,
      value: String(mm),
      label: mm + 'mm',
      sort: mm,
      cm: 0
    };
  }

  /* ======================================================================== *
   *  Collection / search grids — collapse each group to one card             *
   * ======================================================================== */

  // Related/recommendation sliders reuse the same card; never collapse those.
  function inRelatedSlider(el) {
    return !!(el.closest && el.closest('product-recommendations, .related-products'));
  }

  // A card carries ONE sku, so a product held in Shopify as a real multi-variant chain
  // or ring (one product, a Length/Size option, several sizes) looks from here exactly
  // like a separated single-size product. Collapsing it into a neighbouring group hid
  // the whole product from the grid — 9KOM30155CM (45/50/55cm) disappeared behind the
  // single 9KOM30142CM, same for 9KBR302. Those products already offer every size on
  // their own page, so grouping has nothing to add: skip them entirely, neither hidden
  // nor decorated. This is what keeps today's rings safe, since most of them still
  // arrive from Unleashed already merged into an Alloy + Size product; only the split
  // ones are grouped here. Cards rendered before data-searay-variants existed report no
  // count and keep the old behaviour.
  function isMultiVariant(card) {
    var n = parseInt(card.dataset.searayVariants, 10);
    return n > 1;
  }

  // The representative card already shown for each group key. Infinite scroll appends
  // the next page's cards later; without this, each page would pick its own
  // representative and a group would reappear once per page. The letter collection
  // (70 products, 36 per page) is where that showed.
  var shownReps = {};

  // data-searay-available on a sold-out card (snippets/card-product.liquid).
  var UNAVAILABLE = 'false';

  // Representative = lowest SKU alphabetically: deterministic, and matches the "first
  // product code" rule used to choose the shared description. Letter groups go by their
  // own order instead so the card is letter A, not the cross (9KCR104 < 9KTFL1A).
  // In-stock members go first, so a group with any stock is never fronted by a
  // "Coming Soon" card (sold-out cards also arrive last: assets/infinite-scroll.js
  // holds them back until the final page).
  function compareMembers(a, b) {
    if (a.available !== b.available) return a.available ? -1 : 1;
    if (a.info.kind === KIND.LETTER) return a.info.sort - b.info.sort;
    return a.sku < b.sku ? -1 : a.sku > b.sku ? 1 : 0;
  }

  function collapseGrid(scope) {
    var cards = (scope || document).querySelectorAll('.card-wrapper[data-searay-sku]');
    if (!cards.length) return;

    var groups = {};
    cards.forEach(function (card) {
      if (card.dataset.searayGrouped === '1') return; // idempotent across re-runs
      if (inRelatedSlider(card)) return;
      if (isMultiVariant(card)) return;
      var info = parse(card.dataset.searaySku, card.dataset.searayType);
      if (!info) return;
      card.dataset.searayGrouped = '1';
      (groups[info.key] = groups[info.key] || []).push({
        card: card,
        cell: card.closest('li') || card,
        info: info,
        // Cards rendered before data-searay-available existed count as in stock.
        available: card.dataset.searayAvailable !== UNAVAILABLE,
        sku: String(card.dataset.searaySku).toUpperCase()
      });
    });

    Object.keys(groups).forEach(function (key) {
      var members = groups[key];
      var shown = shownReps[key];
      // A later page can bring a member that sorts ahead of the card already shown
      // (the letter collection's first page holds the cross but not letter A), so the
      // shown card competes again and gives way if it is no longer first.
      if (shown && shown.card.isConnected) members.push(shown);
      members.sort(compareMembers);
      var rep = members[0];
      shownReps[key] = rep;
      rep.cell.style.display = '';
      members.slice(1).forEach(function (m) { m.cell.style.display = 'none'; });
      decorateRepresentative(rep, members.length);
    });

    // Collapsing can shrink a page of 36 cards to a handful, bringing the infinite-scroll
    // sentinel into range with no scroll ever happening, and assets/infinite-scroll.js only
    // re-checks on scroll. Without this, the letter collection showed page one's five cards
    // (the charm card as the cross) until the visitor happened to scroll.
    if (Object.keys(groups).length) nudgeInfiniteScroll();
  }

  // assets/infinite-scroll.js exposes loadAhead(), which fetches the next pages in
  // parallel straight away. Waiting for `load` instead held the letter collection back
  // behind every third-party app (~2.7s) before page 2 even started.
  // Fallback when loadAhead cannot run: this file is deferred and runs BEFORE
  // DOMContentLoaded, which is when infinite-scroll attaches its scroll listener, so the
  // nudge waits for `load` (always after it).
  function nudgeInfiniteScroll() {
    var scroller = window.SearayInfiniteScroll;
    if (scroller && scroller.loadAhead && scroller.loadAhead()) return;
    var fire = function () { window.dispatchEvent(new Event('scroll')); };
    if (document.readyState === 'complete') fire();
    else window.addEventListener('load', fire, { once: true });
  }

  // The card's SKU line shows the bin-location code (variant metafield
  // custom.bin_location_sku), which is normally shared by every size in a group and
  // carries no size at all (9KCH37145CM -> "CH371"). Where that metafield has drifted
  // and holds the full size-coded SKU instead (18KROP7045CM -> "18KROP7045CM",
  // 9KDR678SIZEQ -> "9KDR678SIZEQ"), a collapsed card would advertise one specific size
  // while its title and note say "a range of sizes". Strip the trailing size code so it
  // reads as the shared base (18KROP70 / 9KDR678), matching what we do to the title.
  // Guarded on the representative's OWN size so a genuine bin code is never touched.
  function stripSizeFromCardSku(card, info) {
    var el = card.querySelector('.product__sku');
    if (!el) return;
    var match = CARD_SKU_MATCH[info.kind];
    if (!match) return;
    var text = el.textContent || '';
    var m = match(text);
    if (!m) return;
    if (CARD_SKU_VALUE[info.kind](m) !== info.value) return;
    var cut = text.slice(0, m.index) + text.slice(m.index + m[0].length);
    el.textContent = tidyBase(info.kind, cut.trim()).trim();
  }

  function decorateRepresentative(rep, count) {
    // The card renders the title in more than one .card__heading (a hidden
    // no-media variant and the visible one), so strip the size from every one.
    var headings = rep.card.querySelectorAll('.card__heading');
    if (!headings.length) return;
    var titleRe = TITLE_RE[rep.info.kind];
    if (rep.info.title) {
      // A letter card stands for every letter, so it takes the group's name outright.
      headings.forEach(function (h) {
        (h.querySelector('a') || h).textContent = rep.info.title;
      });
    } else if (titleRe) {
      headings.forEach(function (h) {
        var link = h.querySelector('a') || h;
        var cleaned = (link.textContent || '').trim().replace(titleRe, '').trim();
        if (cleaned) link.textContent = cleaned;
      });
    }
    stripSizeFromCardSku(rep.card, rep.info);
    if (count > 1 && !rep.card.querySelector('.searay-lengths-note')) {
      var note = document.createElement('span');
      note.className = 'searay-lengths-note';
      // No hard count: a grid only sees the siblings present on THIS page (an OOS or
      // lower-ranked size may be missing). Wording matches Christina's agreed
      // "available in a range of sizes". The product page lists the full set.
      note.textContent = rep.info.kind === KIND.LETTER ? LETTER_CARD_NOTE : SIZE_CARD_NOTE;
      note.style.cssText =
        'display:block;font-size:12px;color:#6b6b6b;margin-top:2px;font-weight:400;';
      headings[headings.length - 1].insertAdjacentElement('afterend', note);
    }
  }

  /* ======================================================================== *
   *  Product page — size selector that navigates between sibling products    *
   * ======================================================================== */

  function trueGrams(variant) {
    if (!variant) return 0;
    var g = Number(variant.weight) / GRAMS_PER_KG;
    if (!(g >= MIN_PLAUSIBLE_GRAMS && g <= MAX_PLAUSIBLE_GRAMS)) return 0;
    return g;
  }

  // "25.65g", "28.4g", "15g" — never a trailing zero, which would read as false
  // precision on a weight the page itself calls approximate.
  function formatGrams(g) {
    if (!g) return '';
    var scale = Math.pow(10, GRAMS_DECIMALS);
    var rounded = Math.round(g * scale) / scale;
    if (rounded % 1 === 0) return rounded + 'g';
    return rounded.toFixed(GRAMS_DECIMALS).replace(/0$/, '') + 'g';
  }

  // This size's own weight, handed over by the server-rendered Specifications row.
  function currentGrams(root) {
    var sectionId = root.dataset.searaySection;
    var cell = sectionId && document.getElementById('searay-spec-weights-' + sectionId);
    var g = cell ? parseFloat(cell.dataset.searayGrams) : NaN;
    return g > 0 ? g : 0;
  }

  // Widen the Specifications table from this one size to the whole group. Both rows
  // are rendered server-side with this product's own size and weight (see
  // sections/main-product-searay.liquid), so a failed lookup still leaves something
  // correct on screen. Driven by the SAME sibling list as the size buttons, so the table
  // can never advertise a size the buttons do not offer.
  function fillSpecRows(root, siblings, info) {
    var sectionId = root.dataset.searaySection;
    if (!sectionId) return;
    // A letter is not a size; those pages carry no "Available sizes" row to widen.
    if (info.kind === KIND.LETTER) return;

    var sizes = document.getElementById('searay-spec-lengths-' + sectionId);
    if (sizes) {
      sizes.textContent = siblings
        .map(function (s) { return s.label; })
        .join(' / ');
    }

    // Only a chain's weight varies with its size. A ring's weight is a property of the
    // piece, so there is no weight row on that page to widen and nothing to fill.
    if (info.kind !== KIND.LENGTH) return;

    var weights = document.getElementById('searay-spec-weights-' + sectionId);
    if (!weights) return;
    var weightsRow = document.getElementById('searay-spec-weightsrow-' + sectionId);
    var anyWeight = siblings.some(function (s) { return s.grams > 0; });
    if (!anyWeight) {
      // No size in the group has a weight on file — leave the row out entirely
      // rather than print a line of dashes.
      if (weightsRow) weightsRow.hidden = true;
      return;
    }
    weights.textContent = siblings
      .map(function (s) { return s.grams > 0 ? formatGrams(s.grams) : MISSING_WEIGHT; })
      .join(' / ');
    if (weightsRow) weightsRow.hidden = false;
    var disclaimer = document.getElementById('searay-spec-weightdisc-' + sectionId);
    if (disclaimer) disclaimer.hidden = false;
  }

  function buildProductSelector(root) {
    var info = parse(root.dataset.searaySku, root.dataset.searayType);
    if (!info) return; // not a size-coded product -> leave as a single product
    var currentUrl = root.dataset.currentUrl || location.pathname;

    fetchSiblings(info)
      .then(function (siblings) {
        // Guarantee the current size is present even if the lookup missed it.
        if (!siblings.some(function (s) { return s.value === info.value; })) {
          siblings.push({
            value: info.value,
            label: info.label,
            sort: info.sort,
            url: currentUrl,
            available: true,
            grams: currentGrams(root)
          });
        }
        // Trust the server for THIS size's weight — it applied the weight-unit check
        // the Ajax endpoint makes impossible, so it is the authoritative figure.
        var ownGrams = currentGrams(root);
        if (ownGrams) {
          siblings.forEach(function (s) {
            if (s.value === info.value) s.grams = ownGrams;
          });
        }
        siblings.sort(function (a, b) { return a.sort - b.sort; });
        if (siblings.length < 2) return; // nothing to choose between
        renderSelector(root, siblings, info, currentUrl);
        fillSpecRows(root, siblings, info);
      })
      .catch(function () { /* silent: page still works as a single product */ });
  }

  // Find sibling products sharing this SKU's group key. The predictive endpoint matches
  // by SKU (via resources[options][fields]) but does not emit the SKU back, so we take
  // the matched product handles from the returned markup and resolve each candidate's
  // real SKU + stock via /products/<handle>.js. This mirrors the store's proven
  // SKU-search path in assets/sku-search-fallback.js.
  function fetchSiblings(info) {
    if (info.kind === KIND.LETTER) return fetchLetterSiblings(info);
    var url =
      PREDICTIVE_URL + '?q=' + encodeURIComponent(info.base) +
      '&resources[type]=product&resources[limit]=12' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=variants.sku,variants.barcode' +
      '&section_id=predictive-search';

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var links = doc.querySelectorAll('a[href*="/products/"]');
        var handles = [];
        var seen = {};
        links.forEach(function (a) {
          var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
          if (m && !seen[m[1]]) { seen[m[1]] = 1; handles.push(m[1]); }
        });
        return Promise.all(handles.map(resolveProduct));
      })
      .then(function (resolved) {
        var seen = {};
        return (resolved || [])
          .filter(Boolean)
          .filter(function (s) { return s.key === info.key; })
          .filter(function (s) {
            if (seen[s.value]) return false; // one product per size
            seen[s.value] = 1;
            return true;
          });
      });
  }

  // Letter groups read the whole family in one request: a collection's products.json, or
  // a search on the SKU prefix rendered through sections/searay-sku-list.liquid. Both
  // carry each product's SKU and stock, so no per-product lookup is needed.
  function fetchLetterSiblings(info) {
    var source = info.family.collectionUrl
      ? fetchCollectionSkus(info.family.collectionUrl)
      : fetchSearchSkus(info.family.searchTerm);
    return source.then(function (products) {
      var seen = {};
      var out = [];
      products.forEach(function (p) {
        if (p.variants !== 1) return;
        var s = parse(p.sku, p.type);
        if (!s || s.key !== info.key || seen[s.value]) return;
        seen[s.value] = 1;
        out.push({
          value: s.value,
          label: s.label,
          sort: s.sort,
          url: '/products/' + p.handle,
          grams: 0,
          available: p.available
        });
      });
      return out;
    });
  }

  // Both sources come back as { handle, sku, type, variants (count), available }.
  function fetchCollectionSkus(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        return (data.products || []).map(function (p) {
          var v = (p.variants || [])[0] || {};
          return {
            handle: p.handle,
            sku: v.sku || v.barcode,
            type: p.product_type,
            variants: (p.variants || []).length,
            available: v.available !== false
          };
        });
      });
  }

  function fetchSearchSkus(term) {
    var url =
      '/search?q=' + encodeURIComponent(term) +
      '&type=product&options[prefix]=last&options[unavailable_products]=last' +
      '&section_id=' + SKU_LIST_SECTION;
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('script[data-searay-sku-list]');
        return el ? JSON.parse(el.textContent) : [];
      });
  }

  function resolveProduct(handle) {
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants || !p.variants.length) return null;
        // Same rule as the grid (see isMultiVariant): a real multi-variant product is not
        // a sibling size, it is a whole product with its own selector. Offering it as one
        // button would advertise its first size and hide the rest.
        if (p.variants.length > 1) return null;
        var v = p.variants[0];
        // /products/<handle>.js carries the product type, which the hoop rule needs.
        var info = parse(v.sku || v.barcode, p.type);
        if (!info) return null;
        return {
          value: info.value,
          label: info.label,
          sort: info.sort,
          key: info.key,
          url: p.url,
          grams: info.kind === KIND.LENGTH ? trueGrams(v) : 0,
          available: v.available !== false && p.available !== false
        };
      })
      .catch(function () { return null; });
  }

  function renderSelector(root, siblings, info, currentUrl) {
    var list = root.querySelector('.searay-group-sizes__list');
    if (!list) return;

    // Preserve the ?view=searay-upgraded preview param while navigating siblings.
    var keepView = /[?&]view=searay-upgraded/.test(location.search)
      ? '?view=searay-upgraded'
      : '';

    if (info.kind === KIND.LETTER) {
      var lbl = root.querySelector('.searay-lbl');
      if (lbl) lbl.textContent = LETTER_SELECTOR_LABEL;
    }

    list.innerHTML = '';
    siblings.forEach(function (s) {
      var isCurrent = s.value === info.value;
      var el = document.createElement(isCurrent ? 'span' : 'a');
      el.className =
        'searay-opt searay-size-btn' +
        (isCurrent ? ' is-active' : '') +
        (s.available ? '' : ' is-oos');
      el.textContent = s.label;
      if (!isCurrent) {
        el.href = String(s.url).split('?')[0] + keepView;
        el.style.textDecoration = 'none';
      }
      list.appendChild(el);
    });
    root.hidden = false;
  }

  /* ======================================================================== *
   *  Init                                                                    *
   * ======================================================================== */

  function init(scope) {
    collapseGrid(scope);
    (scope || document)
      .querySelectorAll('[data-searay-group-root]')
      .forEach(buildProductSelector);
  }

  function ready() {
    init(document);
    // Re-collapse when infinite scroll appends more cards to the collection grid, and when
    // a sort or filter change re-renders it. assets/facets.js replaces the inner HTML of
    // #ProductGridContainer, so #product-grid itself is a new element each time and an
    // observer on it went deaf after the first sort change (the letter collection showed
    // all 81 cards, 30 Sep).
    var grid = document.getElementById('ProductGridContainer') || document.getElementById('product-grid');
    if (grid && 'MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () {
          pending = null;
          collapseGrid(document);
        }, 50);
      }).observe(grid, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  window.SearayGroup = {
    parse: parse,
    classify: classify,
    isHoopType: isHoopType,
    CATEGORY: CATEGORY,
    KIND: KIND
  };
})();
